import _isEmpty from 'lodash/isEmpty';
import getLatestVersionHistoryItem from '../common/getLatestVersionHistoryItem';
import { updateVersionHistoryLatestSavedItem } from './versionHistory';
import eventService from '../common/eventService';

import {
    SOCKET,
    DRAFT_CHANGED,
    DRAFT_SAVED,
    RECONNECT,
    DRAFT_OPENED,
    DISCONNECT,
    UPDATE_CONNECTED_CLIENTS,
    RECONNECT_ATTEMPT,
    RECONNECT_FAILED,
    SEND_UNSAVED_ACTIONS,
    SET_CONNECTED,
} from '../constants/actionTypes/sockets';
import { setLoading } from './session';

/**
 * Sets the realtime server connection status
 * @param {boolean} isConnected Whether the client is connected to the realtime server
 */
export const setConnected = (isConnected) => ({
    type: SET_CONNECTED,
    payload: {
        isConnected,
    },
});

/**
 * Makes the initial connection to the realtime backend
 * @param {string} token User's JWT
 * @param {string} plan Account plan user is on
 * @param {function} dispatch Redux dispatch
 */
export const connectSocket = (token, plan, dispatch) => {
    // Errors here get caught by the sockets middleware .catch block
    const connect = async (socket) => {
        await socket.connect(token, plan);
        dispatch(setConnected(true));
    };

    return {
        type: SOCKET,
        promiseArray: [(socket) => connect(socket)],
    };
};

/**
 * Sends the user's info to the socket server
 * @param {Object} connectedUser User's info
 */
export const draftOpened = (connectedUser) => ({
    type: SOCKET,
    promiseArray: [(socket) => socket.emit(DRAFT_OPENED, { connectedUser })],
});

/**
 * Listen for reconnect event to fire off DRAFT_OPENED again
 * @param {Object} connectedUser User's info
 * @param {function} dispatch Redux's dispatch
 */
export const listenForReconnect = (connectedUser, dispatch) => {
    const reconnect = () => {
        dispatch(draftOpened(connectedUser));
        dispatch(setConnected(true));
    };

    const reconnecting = (attemptNumber) => {
        if (attemptNumber === 1) {
            dispatch(setLoading(false));
        }
    };

    // Passing socket.disconnect directly to the on listener results in an undefined socket
    const disconnect = (socket) => () => {
        dispatch(setConnected(false));
        socket.disconnect();
    };

    return {
        type: SOCKET,
        promiseArray: [
            (socket) => socket.on(RECONNECT_ATTEMPT, reconnecting),
            (socket) => socket.on(RECONNECT, reconnect),
            (socket) => socket.on(RECONNECT_FAILED, disconnect(socket)),
        ],
    };
};

/**
 * Add listener for connecting clients (excluding self)
 * @param {function} dispatch Redux's dispatch
 */
export const listenForClients = (dispatch) => {
    const updateConnectedClients = (users, id) =>
        dispatch({
            type: UPDATE_CONNECTED_CLIENTS,
            payload: { users, currentUserSocketId: id },
        });

    return {
        type: SOCKET,
        promiseArray: [
            (socket) => socket.on(UPDATE_CONNECTED_CLIENTS, (users) => updateConnectedClients(users, socket.socket.id)),
        ],
    };
};

/**
 * Add listener for receiving unsaved actions
 * @param {function} dispatch Redux's dispatch
 */
export const listenForUnsavedChanges = (dispatch) => {
    const requestForUnsavedActions = (actions = []) => {
        actions.forEach((action) => {
            dispatch(action);
        });
        dispatch(setLoading(false));
    };

    return {
        type: SOCKET,
        promiseArray: [(socket) => socket.on(SEND_UNSAVED_ACTIONS, requestForUnsavedActions)],
    };
};

/**
 * Add listener for draft changes
 * @param {function} dispatch Redux's dispatch
 */
export const listenForChanges = (dispatch) => {
    const dispatchChanges = (action) => dispatch(action);
    return {
        type: SOCKET,
        promiseArray: [(socket) => socket.on(DRAFT_CHANGED, dispatchChanges)],
    };
};

/**
 * Lets the server know a save in the draft happened so the server broadcasts it
 */
export const draftSaved = () => ({
    type: SOCKET,
    promiseArray: [(socket) => socket.emit(DRAFT_SAVED)],
});

/**
 * Add listener for draft saves and fetch the latest saved version
 * @param {object} args
 * @param {string} args.documentID - Document identification composed of ${d:t}${draftID}${versionID}${trackID}
 * @param {function} args.dispatch Redux's dispatch
 * @param {boolean} [args.syncsChanges] - whether this client receives other clients' edits live
 */
export const listenForSaves = ({ documentID, dispatch, syncsChanges = false }) => {
    // After a disconnect this client may have missed other clients' edits and saves, and a
    // reconnect does not replay them, so this stays set for the session: the integration then
    // blocks a conflicting save instead of resolving it against content that may be stale.
    let liveSyncLost = false;
    const onDisconnect = () => {
        if (syncsChanges && !liveSyncLost) {
            liveSyncLost = true;
            eventService.sendLiveSyncLost();
        }
    };
    const dispatchFetchLatestSavedItem = async () => {
        if (syncsChanges && !liveSyncLost) {
            eventService.sendRemoteSaved();
        }
        // ${d:t}${draftID}${versionID}${trackID}
        if (documentID.length >= 19) {
            const latestItem = await getLatestVersionHistoryItem(documentID);
            if (!_isEmpty(latestItem)) {
                dispatch(updateVersionHistoryLatestSavedItem(latestItem));
            }
        }
    };
    return {
        type: SOCKET,
        promiseArray: [
            (socket) => socket.on(DRAFT_SAVED, dispatchFetchLatestSavedItem),
            (socket) => socket.on(DISCONNECT, onDisconnect),
        ],
    };
};
